import { getDocument } from "@faire/web/common/globals/getDocument";
import {
  useFloating,
  size,
  hide,
  useInteractions,
  useDismiss,
  offset,
  autoUpdate,
  flip,
} from "@floating-ui/react";
import * as React from "react";

import { IProps } from "./Popover";

const ABOVE_MODAL_Z_INDEX = 1050;
const DEFAULT_Z_INDEX = 5;

interface IPopoverContext extends IProps {
  floatingData: ReturnType<typeof useFloating>;
  floatingInteractions: ReturnType<typeof useInteractions>;
  bodyRef: React.MutableRefObject<HTMLElement | null>;
  popoverZIndex: number;
}

const PopoverContext = React.createContext<IPopoverContext | undefined>(
  undefined
);

export const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);

  if (!context) {
    throw new Error("usePopoverContext must be used within a PopoverProvider");
  }

  return context;
};

export const PopoverProvider: React.FC<IProps> = ({ children, ...props }) => {
  const {
    open,
    anchorRef,
    placement = "bottom-start",
    zIndex,
    isModal,
    mainAxisOffset,
    crossAxisOffset,
    width,
    height,
    disableFlip,
    onClickOutside,
  } = props;

  const bodyRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    bodyRef.current = getDocument()?.body ?? null;
  }, []);

  const enableFlip = !disableFlip && !!height; // Flip requires height to work
  const popoverZIndex =
    zIndex ?? (isModal ? ABOVE_MODAL_Z_INDEX : DEFAULT_Z_INDEX);

  const floatingData = useFloating({
    ...(anchorRef ? { elements: { reference: anchorRef } } : {}),
    placement,
    middleware: [
      size({
        apply({ rects, elements }) {
          elements.floating.style.width = width
            ? `${width}px`
            : `${rects.reference.width}px`;
          if (height) {
            elements.floating.style.height = `${height}px`;
          }
        },
      }),
      ...(enableFlip ? [flip({ fallbackStrategy: "initialPlacement" })] : []),
      offset({
        mainAxis: mainAxisOffset ?? 0,
        crossAxis: crossAxisOffset ?? 0,
      }),
      hide({
        strategy: "escaped",
      }),
      hide({
        strategy: "referenceHidden",
      }),
    ],
    open,
    onOpenChange: onClickOutside,
    whileElementsMounted: autoUpdate,
  });

  const floatingInteractions = useInteractions([
    useDismiss(floatingData.context),
  ]);

  return (
    <PopoverContext.Provider
      value={{
        ...props,
        floatingData,
        floatingInteractions,
        bodyRef,
        popoverZIndex,
      }}
    >
      {children}
    </PopoverContext.Provider>
  );
};
